import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  PoBreadcrumb,
  PoModalComponent,
  PoNotificationService,
  PoTableAction,
  PoTableColumn
} from '@po-ui/ng-components';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PendenciaEstadualDocumentoDto,
  PendenciaEstadualService
} from '../pendencia-estadual.service';
import {
  COMPETENCIA_RELATORIO_OPTIONS
} from '../rpa-relatorio-pendencias.config';
import {
  TagChipStyle,
  TagFiltroChip,
  RELATORIO_TAG_FILTER_OPTIONS,
  RELATORIO_TAG_FILTER_OPTIONS_INSCRITA,
  filtrarPorTagsAnalise,
  filtrarPorTagsInscrita,
  formatarLabelTag,
  isTagCnpj,
  montarFiltrosTagsEstaticos,
  normalizarConjuntoTags,
  resolverEstiloTag,
  estiloChipNgStyle
} from './analise-pendencia-tags.config';

interface TagBadge {
  label: string;
  estilo: TagChipStyle;
}

interface EmpresaPorCnpjDto {
  cnpj?: string;
  Cnpj?: string;
  razaoSocial?: string;
  RazaoSocial?: string;
  codDominio?: number | null;
  CodDominio?: number | null;
}

interface RpaProcessoRow {
  id: string;
  executionId: string;
  competencia: string;
  fileName: string;
  cnpj: string;
  razaoSocial: string;
  codDominio: string;
  contentType: string;
  storagePath: string;
  sizeBytes: number;
  sizeFormatted: string;
  uploadedAt: string;
  tags: string[];
  raw?: unknown;
}

type TipoAnalisePendencia = 'nao-inscrita' | 'inscrita';

const BOT_ID_POR_TIPO: Record<TipoAnalisePendencia, string> = {
  'nao-inscrita': 'pendencia-nao-inscrita-estadual',
  inscrita: 'pendencia-inscrita-estadual'
};

const PAGE_SIZE_DOCUMENTOS = 20;

@Component({
  selector: 'app-rpas-analise-pendencia',
  templateUrl: './rpas-analise-pendencia.component.html',
  styleUrls: ['./rpas-analise-pendencia.component.css'],
  standalone: false
})
export class RPAsAnalisePendenciaComponent implements OnInit {
  @ViewChild('modalRelatorioPendencias') modalRelatorioPendencias!: PoModalComponent;

  carregando = false;
  loadingShowMore = false;
  loadingRelatorioPendencias = false;
  processos: RpaProcessoRow[] = [];
  tagsFiltro: TagFiltroChip[] = [];
  filtrosTagsAtivos: Record<string, boolean> = {};
  tipoAnalise: TipoAnalisePendencia = 'nao-inscrita';
  page = 1;
  moreItens = false;

  competenciaRelatorio = '';

  readonly competenciaRelatorioOptions = COMPETENCIA_RELATORIO_OPTIONS;

  readonly columns: PoTableColumn[] = [
    { property: 'razaoSocial', label: 'Razão social' },
    { property: 'codDominio', label: 'Código do domínio', width: '170px' },
    { property: 'fileName', label: 'Nome do arquivo' },
    {
      property: 'uploadedAt',
      label: 'Enviado em',
      type: 'date',
      format: 'dd/MM/yyyy HH:mm'
    },
    { property: 'tags', label: 'Tags', type: 'columnTemplate' }
  ];

  private readonly empresasApi = `${environment.apiUrl}/v1/empresas`;
  private readonly cacheEmpresasPorCnpj = new Map<
    string,
    { razaoSocial: string; codDominio: string }
  >();

  readonly tableActions: PoTableAction[] = [
    {
      label: 'Visualizar arquivo',
      action: this.visualizarArquivo.bind(this),
      icon: 'an an-eye',
      visible: (row: RpaProcessoRow) => this.podeVisualizarArquivo(row)
    },
    // Força menu ⋮ (com 1 ação o PO UI mostra o botão direto na coluna).
    {
      label: '',
      action: () => undefined,
      visible: () => false
    }
  ];

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Legalização' },
      { label: 'Pendência estadual' }
    ]
  };

  constructor(
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly pendenciaEstadualService: PendenciaEstadualService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  irConsultaPendencia(): void {
    this.router.navigate(['relatorio-pendencias/consulta']);
  }

  ngOnInit(): void {
    this.atualizarFiltrosTags();
    this.loadMore(true);
  }

  get itensFiltrados(): RpaProcessoRow[] {
    return this.processos.filter((item) => this.itemVisivelPorTags(item));
  }

  get possuiFiltrosTags(): boolean {
    return this.tagsFiltro.length > 0;
  }

  get isPendenciaInscrita(): boolean {
    return this.tipoAnalise === 'inscrita';
  }

  get tituloTipoAnalise(): string {
    return this.isPendenciaInscrita
      ? 'Análise de pendências inscritas'
      : 'Análise de pendências não inscritas';
  }

  get descricaoTipoAnalise(): string {
    return this.isPendenciaInscrita ? 'inscrita' : 'não inscrita';
  }

  get mensagemListaVazia(): string {
    return this.isPendenciaInscrita
      ? 'Nenhum documento de pendência inscrita estadual encontrado.'
      : 'Nenhum documento de pendência não inscrita estadual encontrado.';
  }

  selecionarTipoAnalise(tipo: TipoAnalisePendencia): void {
    if (this.tipoAnalise === tipo || this.carregando || this.loadingShowMore) return;

    this.tipoAnalise = tipo;
    this.filtrosTagsAtivos = {};
    this.atualizarFiltrosTags();
    this.loadMore(true);
  }

  loadMore(reset = false): void {
    if (this.carregando || this.loadingShowMore) return;

    if (reset) {
      this.page = 1;
      this.processos = [];
      this.moreItens = false;
      this.carregando = true;
      this.cacheEmpresasPorCnpj.clear();
    } else {
      this.loadingShowMore = true;
    }

    const botId = BOT_ID_POR_TIPO[this.tipoAnalise];

    this.pendenciaEstadualService
      .getList(this.page, PAGE_SIZE_DOCUMENTOS, botId)
      .subscribe({
        next: async (response) => {
          try {
            const pagina = (response.items ?? []).map(
              (item: PendenciaEstadualDocumentoDto) => this.mapearDocumento(item)
            );
            await this.enriquecerComEmpresas(pagina);

            this.processos = reset ? pagina : [...this.processos, ...pagina];
            this.moreItens = !!response.hasNext;
            this.page++;
            this.atualizarFiltrosTags();

            if (reset && this.processos.length === 0) {
              this.poNotificationService.information(this.mensagemListaVazia);
            }
          } finally {
            this.carregando = false;
            this.loadingShowMore = false;
          }
        },
        error: () => {
          this.carregando = false;
          this.loadingShowMore = false;
          if (reset) {
            this.processos = [];
            this.moreItens = false;
            this.atualizarFiltrosTags();
          }
          this.poNotificationService.error('Erro ao carregar os documentos de pendência.');
        }
      });
  }

  abrirRelatorioPendencias(): void {
    this.competenciaRelatorio = '';
    this.modalRelatorioPendencias.open();
  }

  fecharRelatorioPendencias(): void {
    this.modalRelatorioPendencias.close();
  }

  onChangeCompetenciaRelatorio(value: string): void {
    this.competenciaRelatorio = value;
  }

  async gerarRelatorioPendencias(): Promise<void> {
    if (!this.competenciaRelatorio) {
      this.poNotificationService.warning('Selecione a competência (mês/ano).');
      return;
    }

    if (
      this.tagsFiltro.length > 0 &&
      !this.tagsFiltro.some((chip) => this.filtrosTagsAtivos[chip.tag])
    ) {
      this.poNotificationService.warning('Selecione ao menos uma tag para filtrar.');
      return;
    }

    this.loadingRelatorioPendencias = true;

    try {
      const tagsAtivas = this.tagsFiltro
        .filter((chip) => this.filtrosTagsAtivos[chip.tag])
        .map((chip) => chip.tag);

      const blob = await firstValueFrom(
        this.pendenciaEstadualService.gerarRelatorio({
          botId: BOT_ID_POR_TIPO[this.tipoAnalise],
          competencia: this.competenciaRelatorio,
          tags: tagsAtivas,
          totalTagsFiltro: this.tagsFiltro.length
        })
      );

      const tipoArquivo = this.isPendenciaInscrita ? 'inscrita' : 'nao-inscrita';
      const nomeArquivo = `pendencias-estadual-${tipoArquivo}-${this.competenciaRelatorio}.zip`;
      this.downloadBlob(blob, nomeArquivo);
      this.fecharRelatorioPendencias();
      this.poNotificationService.success('Relatório gerado com sucesso.');
    } catch (error) {
      const mensagem = await this.extrairMensagemErroRelatorio(error);
      this.poNotificationService.error(mensagem);
    } finally {
      this.loadingRelatorioPendencias = false;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  private async extrairMensagemErroRelatorio(error: unknown): Promise<string> {
    const fallback = 'Erro ao gerar relatório de pendências.';
    if (!(error instanceof HttpErrorResponse) || !error.error) {
      return fallback;
    }

    try {
      if (error.error instanceof Blob) {
        const texto = await error.error.text();
        const parsed = JSON.parse(texto) as { error?: string };
        return parsed.error?.trim() || fallback;
      }

      if (typeof error.error === 'object' && error.error?.error) {
        return String(error.error.error);
      }
    } catch {
      // ignore
    }

    return fallback;
  }

  alternarFiltroTag(tag: string): void {
    this.filtrosTagsAtivos = {
      ...this.filtrosTagsAtivos,
      [tag]: !this.filtrosTagsAtivos[tag]
    };
  }

  filtroTagAtivo(tag: string): boolean {
    return !!this.filtrosTagsAtivos[tag];
  }

  estiloChip(tagFiltro: TagFiltroChip | TagBadge): Record<string, string> {
    return estiloChipNgStyle(tagFiltro.estilo);
  }

  tagsParaBadges(tags: string[] | null | undefined): TagBadge[] {
    if (!Array.isArray(tags) || tags.length === 0) return [];

    const tagsVisiveis = this.isPendenciaInscrita
      ? tags.filter((tag) => !isTagCnpj(tag))
      : tags;

    return tagsVisiveis.map((tag, indice) => ({
      label: formatarLabelTag(tag),
      estilo: resolverEstiloTag(tag, indice)
    }));
  }

  podeVisualizarArquivo(row: RpaProcessoRow): boolean {
    return !!row.id;
  }

  visualizarArquivo(row: RpaProcessoRow): void {
    if (!row.id) {
      this.poNotificationService.warning('Arquivo ainda não disponível.');
      return;
    }

    this.pendenciaEstadualService.getFile(row.id).subscribe({
      next: (file) => {
        const url = URL.createObjectURL(file);
        const openedWindow = window.open(url, '_blank');

        if (!openedWindow) {
          URL.revokeObjectURL(url);
          this.poNotificationService.warning(
            'O navegador bloqueou a abertura do arquivo. Libere pop-ups e tente novamente.'
          );
          return;
        }

        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      },
      error: () => this.poNotificationService.error('Erro ao carregar o arquivo.')
    });
  }

  private atualizarFiltrosTags(): void {
    const opcoes = this.isPendenciaInscrita
      ? RELATORIO_TAG_FILTER_OPTIONS_INSCRITA
      : RELATORIO_TAG_FILTER_OPTIONS;

    const { chips, filtrosAtivos } = montarFiltrosTagsEstaticos(
      opcoes,
      this.filtrosTagsAtivos
    );

    // Contagem só dos documentos já carregados na listagem (não exige varrer tudo).
    this.tagsFiltro = chips.map((chip) => {
      const chipNormalizado = normalizarConjuntoTags([chip.tag])[0];
      const count = this.processos.filter((processo) =>
        normalizarConjuntoTags(processo.tags).includes(chipNormalizado)
      ).length;

      return { ...chip, count };
    });
    this.filtrosTagsAtivos = filtrosAtivos;
  }

  private itemVisivelPorTags(item: RpaProcessoRow): boolean {
    if (this.isPendenciaInscrita) {
      return filtrarPorTagsInscrita(item.tags, this.tagsFiltro, this.filtrosTagsAtivos);
    }
    return filtrarPorTagsAnalise(item.tags, this.tagsFiltro, this.filtrosTagsAtivos);
  }

  private mapearDocumento(
    documento: PendenciaEstadualDocumentoDto
  ): RpaProcessoRow {
    const tags = Array.isArray(documento.tags)
      ? documento.tags.map((tag) => String(tag ?? '').trim()).filter(Boolean)
      : [];
    const fileName = documento.fileName || '—';
    const cnpj = this.extrairCnpjDoNomeArquivo(fileName);
    const empresaCache = cnpj ? this.cacheEmpresasPorCnpj.get(cnpj) : undefined;

    return {
      id: documento.id,
      executionId: documento.executionId,
      competencia: this.extrairCompetencia(documento.uploadedAt),
      fileName,
      cnpj,
      razaoSocial: empresaCache?.razaoSocial || '—',
      codDominio: empresaCache?.codDominio || '—',
      contentType: documento.contentType || '—',
      storagePath: documento.storagePath,
      sizeBytes: documento.sizeBytes,
      sizeFormatted: this.formatarTamanho(documento.sizeBytes),
      uploadedAt: documento.uploadedAt,
      tags,
      raw: documento
    };
  }

  private extrairCnpjDoNomeArquivo(fileName: string): string {
    const match = String(fileName ?? '').match(/^(\d{14})_/);
    return match?.[1] ?? '';
  }

  private async enriquecerComEmpresas(rows: RpaProcessoRow[]): Promise<void> {
    const cnpjsPendentes = [
      ...new Set(
        rows
          .map((row) => row.cnpj)
          .filter((cnpj) => !!cnpj && !this.cacheEmpresasPorCnpj.has(cnpj))
      )
    ];

    if (cnpjsPendentes.length > 0) {
      try {
        const empresas = await firstValueFrom(
          this.http.post<EmpresaPorCnpjDto[]>(
            `${this.empresasApi}/by-cnpjs`,
            cnpjsPendentes
          )
        );

        for (const empresa of empresas ?? []) {
          const cnpj = String(empresa.cnpj ?? empresa.Cnpj ?? '').replace(/\D/g, '');
          if (!cnpj) continue;

          const razaoSocial = String(
            empresa.razaoSocial ?? empresa.RazaoSocial ?? ''
          ).trim();
          const codDominioRaw = empresa.codDominio ?? empresa.CodDominio;
          const codDominio =
            codDominioRaw != null && Number(codDominioRaw) > 0
              ? String(codDominioRaw)
              : '—';

          this.cacheEmpresasPorCnpj.set(cnpj, {
            razaoSocial: razaoSocial || '—',
            codDominio
          });
        }

        for (const cnpj of cnpjsPendentes) {
          if (!this.cacheEmpresasPorCnpj.has(cnpj)) {
            this.cacheEmpresasPorCnpj.set(cnpj, {
              razaoSocial: '—',
              codDominio: '—'
            });
          }
        }
      } catch {
        for (const cnpj of cnpjsPendentes) {
          this.cacheEmpresasPorCnpj.set(cnpj, {
            razaoSocial: '—',
            codDominio: '—'
          });
        }
      }
    }

    for (const row of rows) {
      if (!row.cnpj) {
        row.razaoSocial = '—';
        row.codDominio = '—';
        continue;
      }

      const empresa = this.cacheEmpresasPorCnpj.get(row.cnpj);
      row.razaoSocial = empresa?.razaoSocial || '—';
      row.codDominio = empresa?.codDominio || '—';
    }
  }

  private extrairCompetencia(uploadedAt: string): string {
    const data = new Date(uploadedAt);
    if (Number.isNaN(data.getTime())) return '';

    const mes = String(data.getMonth() + 1).padStart(2, '0');
    return `${mes}-${data.getFullYear()}`;
  }

  private formatarTamanho(sizeBytes: number): string {
    if (sizeBytes < 1024) return `${sizeBytes} B`;
    if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
